import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import * as AppConstants from "AppConstants";
import { handleAxiosError } from "app/services/app.error.handler";
import { LeavePlanDataGridBase } from "app/components/leave-plans/LeavePlanDataGridBase";
import { isEmpty, isNumber } from "utils/validationUtil";
import { loadUnifiedLeave } from "app/services/report.service";
import { useDispatch, useSelector } from "react-redux";
import { leaveActions } from "store/leave-slice";

/**
 * Resolves the caller's role and context to the shared effort filter.
 *
 * Replaces four role-specific endpoints — /leave-plans/{all,by-manager,by-member,by-client} — each
 * of which accepted only a date range. Scope now travels as filter parameters, so this surface
 * gains client and member filtering and can never disagree with the consolidated report about who
 * is in scope.
 *
 * Returns null when the context is not yet resolved, so the caller can skip the request rather
 * than send an unscoped one (which the backend rejects).
 *
 * `selection` carries the card's own filter (HRMS-264): a chosen client narrows the role's client
 * scope, and `ownerParams` ({ ownerIds, ownerType } from resolveOwnerParams) pins one person.
 * Narrowing rather than replacing is what stops the filter widening scope — a manager choosing a
 * client still only sees their own subtree on it.
 * @since NEXT_VERSION
 */
export const resolveLeaveFilter = (role, context, fromDate, toDate, selection = {}) => {
  const base = { fromDate, toDate };
  const chosenClientId = selection?.clientId ?? null;
  const ownerParams = selection?.ownerParams ?? {};
  // A chosen person overrides the role's default owner constraint; an empty object leaves it alone.
  const withOwner = (filter) => ({ ...filter, ...ownerParams });

  switch (role?.toUpperCase()) {
    case AppConstants.ROLE_ADMIN: {
      // Admin sees every client; an explicit list keeps the query scoped rather than unbounded.
      // Checked by length rather than isEmpty, which does not treat [] as empty.
      const clientIds = context?.clientIds;
      if (chosenClientId != null) return withOwner({ ...base, clientIds: [chosenClientId] });
      return Array.isArray(clientIds) && clientIds.length > 0 ? withOwner({ ...base, clientIds }) : null;
    }

    case AppConstants.ROLE_EMPLOYEE_MANAGER: {
      // Every manager record the person holds, not just the first: a manager holds one record per
      // client and an L1 may report through any of them, so rooting the subtree at a single record
      // hid the L1s (and their members) reached only through another (HRMS-277). The single-record
      // { id } shape still resolves, so an existing caller keeps working.
      const explicitIds = Array.isArray(context?.managerIds)
        ? context.managerIds.filter((id) => id != null)
        : [];
      const managerIds =
        explicitIds.length > 0
          ? explicitIds
          : !isEmpty(context) && context.hasOwnProperty("id") && isNumber(parseInt(context.id))
          ? [context.id]
          : [];
      if (managerIds.length === 0) return null;
      // clientIds only when one is chosen — the manager's subtree is the scope, and an explicit
      // null would travel into the query string for no reason.
      return withOwner({
        ...base,
        managerIds,
        ...(chosenClientId != null ? { clientIds: [chosenClientId] } : {}),
      });
    }

    case AppConstants.ROLE_EMPLOYEE_MEMBER:
      if (!(!isEmpty(context) && context.hasOwnProperty("memberId") && isNumber(context.memberId))) return null;
      return {
        ...base,
        clientIds: context.clientId != null ? [context.clientId] : null,
        ownerIds: [context.memberId],
        ownerType: AppConstants.OWNER_TYPE_MEMBER,
      };

    case AppConstants.ROLE_CLIENT:
      if (!(!isEmpty(context) && context.hasOwnProperty("clientId") && isNumber(context.clientId))) return null;
      return withOwner({ ...base, clientIds: [context.clientId] });

    default:
      return null;
  }
};

/**
 * `reloadToken` is an opaque counter the caller bumps to force a refetch. It never reaches the
 * request — it only widens the effect's key so that pressing Apply with the dropdowns untouched
 * still reloads the grid (HRMS-264 review): the filter's value is unchanged, but the rows behind
 * it may not be.
 *
 * The rule for bumping it, since the prop is optional and a caller that omits it silently loses
 * the fix: bump it from a user event only — an Apply press, a save, a delete — and NEVER from an
 * effect or a render. A bump driven by anything that itself reacts to a fetch is a refetch loop.
 *
 * Each fetch drives `loading` on the grid, so a press that changes nothing still visibly does
 * something. Requests are not de-duplicated on purpose: a press means "show me the current rows",
 * and an in-flight request issued before the user added a leave cannot answer that.
 */
export const LeavePlansBetweenDates = (props) => {
  const dispatch = useDispatch();
  const { fromDate, toDate, role, context, selection, reloadToken = 0 } = props;
  const leavePlansData = useSelector((state) => state.leave.dashboardRows);
  const [loading, setLoading] = useState(false);

  const filter = resolveLeaveFilter(role, context, fromDate, toDate, selection);
  // Keyed on the resolved filter's VALUE, not on the objects it came from. The effect used to
  // depend on [role, fromDate, toDate] only, so a context that resolved AFTER mount — the admin's
  // client list, a manager's own record — never triggered a refetch and the grid stayed
  // permanently empty (HRMS-277). Serialising also avoids refetching when a caller re-renders with
  // a fresh context/selection object holding the same values.
  const filterKey = filter === null ? null : JSON.stringify(filter);

  useEffect(() => {
    if (filterKey === null) {
      dispatch(leaveActions.replaceDashboardData([]));
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);
    loadUnifiedLeave(JSON.parse(filterKey))
      .then((response) => {
        if (ignore) return;
        dispatch(leaveActions.replaceDashboardData(
          response.status === 204 || !Array.isArray(response?.data) ? [] : response.data
        ));
      })
      .catch((error) => {
        if (ignore) return;
        dispatch(leaveActions.replaceDashboardData([]));
        handleAxiosError(error);
      })
      // Skipped on a superseded run, so a stale completion cannot clear the flag while the
      // request that replaced it is still in flight (and cannot set state after unmount).
      .finally(() => {
        if (ignore) return;
        setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [filterKey, reloadToken, dispatch]);

  return <LeavePlanDataGridBase leavePlansData={leavePlansData} loading={loading} />;
};

LeavePlansBetweenDates.propTypes = {
  role: PropTypes.string.isRequired,
  reloadToken: PropTypes.number,
};
